import { debounce } from "lodash";
import type { FC, RefObject, Dispatch, SetStateAction } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type AutocompleteLocation,
  autocomplete,
} from "../../../assets/Utils/search";
import { useSearch } from "../../hooks/useSearch";
import { ensureInView } from "./utils";
import { cn } from "../../../assets/Utils/generic";
import DestinationModal from "./DestinationModal";

const DestinationInput: FC<{
  mode: "mobile" | "desktop";
  setIsCalendarOpen: Dispatch<SetStateAction<boolean>>;
  setIsGuestsOpen: Dispatch<SetStateAction<boolean>>;
  setIsLocationOpen: Dispatch<SetStateAction<boolean>>;
  isLocationOpen: boolean;
  locationInputRef: RefObject<HTMLDivElement>;
  parent: "home" | "nav" | "detail";
}> = ({
  mode,
  setIsCalendarOpen,
  setIsGuestsOpen,
  setIsLocationOpen,
  isLocationOpen,
  locationInputRef,
  parent,
}) => {
  const { t } = useTranslation();
  const { searchParameters, setParameters } = useSearch();

  const [shouldOpenOver, setShouldOpenOver] = useState(false);
  const [inputText, setInputText] = useState("");
  const [locations, setLocations] = useState<AutocompleteLocation[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const locationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (searchParameters.destinationLabel)
      setInputText(searchParameters.destinationLabel);
  }, [searchParameters.destinationLabel]);

  useEffect(() => {
    if (searchParameters.tag)
      setInputText(t(`ds.destination_category_${searchParameters.tag}`));
  }, [searchParameters.tag]);

  const searchLocation = async (query: string) => {
    setIsLoading(true);
    try {
      const data = await autocomplete(query);
      setLocations(data);
    } catch (error) {
      console.log(error);
    }
    setIsLoading(false);
  };

  const searchCallbackDebounced = useRef(debounce(searchLocation, 1000));

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        locationRef.current &&
        !locationRef.current.contains(event.target as Node)
      ) {
        setIsLocationOpen(false);
      }
    }

    if (isLocationOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isLocationOpen]);

  const handleScroll = () => {
    const input = document.getElementById("search-location-input");
    const navH = document.querySelector("nav")?.clientHeight ?? 0;
    if (input) {
      const rect = input.getBoundingClientRect();
      // 400 px is the height of the dates calendar, used as referecne for the location dropdown since its height is dynamic
      setShouldOpenOver(
        rect.bottom + 366 > window.innerHeight && rect.top - 366 > navH,
      );
    }
  };

  const destinationInput = (isMobile = false) => {
    return (
      <>
        <input
          autoComplete="off"
          id={
            isMobile
              ? "search-location-input-mobile"
              : "search-location-input-desktop"
          }
          style={{
            border: "unset",
            margin: "0",
            padding: "0",
            outline: "none", // Remove focus border
            background: "transparent", // Optional: Ensure no background styles
            color: "black", // Ensures input text inherits the parent's color
            fontSize: mode === "mobile" ? "16px !important" : "",
            width: "100%",
          }}
          placeholder={searchParameters.searchType === "box" ? t("ds.map_area") : t("ds.destination_ph")}
          value={inputText}
          className={cn(
            "medium text-nowrap",
            mode === "desktop" ? "fs-body" : "fs-body",
            parent !== "home" && "text-sm lg:w-[20ch]",
            searchParameters.searchType === "box" && "placeholder:text-black",
          )}
          type="custom"
          onChange={(e) => {
            setInputText(e.target.value);
            if (e.target.value !== "") {
              setIsLocationOpen(true);
              setIsLoading(true);
              searchCallbackDebounced.current(e.target.value);
            }
          }}
        />
      </>
    );
  };

  useEffect(() => {
    handleScroll();
    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (searchParameters.searchType === "box") {
      setInputText("");
    }
  }, [searchParameters.searchType]);

  const locationsPerCategory = useMemo(() => {
    if (!locations) {
      return {
        places: [] as AutocompleteLocation[],
        structures: [] as AutocompleteLocation[],
        extended: [] as AutocompleteLocation[],
      };
    }
    return locations.reduce(
      (acc, location) => {
        if (location.placeId) {
          if (location.nearby || location.extra) {
            acc["extended"].push(location);
            return acc;
          }

          acc["places"].push(location);
          return acc;
        }

        acc["structures"].push(location);
        return acc;
      },
      {
        places: [] as AutocompleteLocation[],
        structures: [] as AutocompleteLocation[],
        extended: [] as AutocompleteLocation[],
      },
    );
  }, [locations]);

  return (
    <div
      className="flex relative"
      style={{
        padding: mode === "mobile" ? "8px 24px" : "0",
        ...(parent === "home"
          ? {
              flexGrow: 1,
              flexBasis: 0,
              minWidth: 0,
            }
          : {}),
      }}
      ref={locationInputRef}
    >
      <div
        id="search-location-container"
        className="h100 w100 d-flex gap-2 cursor-pointer"
        onClick={() => {
          setIsLocationOpen(!isLocationOpen);
          setInputText("");
          setParameters({
            ...searchParameters,
            destinationLabel: "",
            placeId: undefined,
            collectionId: undefined,
          });
          setLocations([]);

          // Usa setTimeout per assicurarsi che il focus avvenga dopo il render
          setTimeout(() => {
            // Determina quale input selezionare in base alla modalità (desktop o mobile)
            const isMobileView = window.innerWidth < 768; // 768px è il breakpoint standard per md in Bootstrap
            const inputId = isMobileView
              ? "search-location-input-mobile"
              : "search-location-input-desktop";
            const inputElement = document.getElementById(
              inputId,
            ) as HTMLInputElement | null;

            if (inputElement) {
              inputElement.focus();
            }

            if (parent === "home" && locationRef.current) {
              ensureInView(locationRef);
            }
          }, 0);
        }}
      >
        {/* Mobile */}
        <div className="d-md-none d-flex flex-column gap-1 w100">
          <p className={"fs-body-sm dark-grey-color"}>{t("ds.destination")}</p>
          <div className="d-flex gap-2 w100">
            <p className="text-black text-base font-medium">
              {searchParameters.searchType === "tag"
                ? t(`ds.destination_category_${searchParameters.tag}`)
                : (!!searchParameters.destinationLabel &&
              searchParameters.destinationLabel !== "")
                ? searchParameters.destinationLabel
                : searchParameters.searchType === "box" ? t("ds.map_area") : t("ds.destination_ph")}
            </p>
          </div>
        </div>
        {/* Desktop */}
        <div
          data-focused={isLocationOpen}
          className={cn(
            "d-none d-md-flex flex-column w100 h100 justify-content-center gap-1 lg:py-2 px-3 rounded-2xl data-[focused=true]:bg-[#F8F8F8] hover:bg-[#F8F8F8] transition-colors duration-300",
            parent !== "home" && "data-[focused=true]:bg-transparent",
          )}
        >
          {parent === "home" && (
            <p
              className={`${
                mode === "desktop" ? "fs-body-sm" : "fs-body-sm"
              } dark-grey-color`}
            >
              {t("ds.destination")}
            </p>
          )}
          {destinationInput(false)}
        </div>
      </div>
      <DestinationModal
        isLocationOpen={isLocationOpen}
        locationRef={locationRef}
        shouldOpenOver={shouldOpenOver}
        mode={mode}
        inputText={inputText}
        setInputText={setInputText}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        searchCallbackDebounced={searchCallbackDebounced}
        setIsLocationOpen={setIsLocationOpen}
        locations={locations}
        locationsPerCategory={locationsPerCategory}
        setIsCalendarOpen={setIsCalendarOpen}
        setIsGuestsOpen={setIsGuestsOpen}
        parent={parent}
      />
    </div>
  );
};

export default DestinationInput;
